/**
 * Crear el producto que el remito trae y el catalogo no tiene.
 *
 * Lo minimo para vender: nombre, rubro y precio. El nombre viene propuesto en
 * el formato del catalogo; el rubro, sugerido por las otras lineas; el codigo
 * interno, propuesto por la base (AC-115) y editable. Lo unico que el operador
 * tiene que pensar de verdad es el precio, y para eso ve el resultado en vivo.
 */
import { useMemo, useState } from "react";
import { calcularPrecio, type Moneda } from "@tora/shared";

import { Importe, InputImporte } from "@/components/money";
import { Campo, CampoCantidad } from "@/components/catalogo/Campos";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { aNumero, type Rubro } from "@/lib/tipos";

import { nombreCatalogo, validarAlta, type DatosAlta, type FilaCompra } from "./logica";

const SIN_RUBRO = "ninguno";

type ModoPrecio = "precio" | "margen";

const MODOS: ReadonlyArray<{ valor: ModoPrecio; etiqueta: string }> = [
  { valor: "precio", etiqueta: "Precio fijo" },
  { valor: "margen", etiqueta: "Margen %" },
];

export interface AltaProductoProps {
  fila: FilaCompra;
  rubros: ReadonlyArray<Rubro>;
  rubroSugeridoId: number | null;
  cotizacion: number;
  costoProducto: { costo: number; moneda: Moneda };
  guardando: boolean;
  onCrear: (alta: DatosAlta) => void;
  onCancelar: () => void;
}

export function AltaProducto({
  fila,
  rubros,
  rubroSugeridoId,
  cotizacion,
  costoProducto,
  guardando,
  onCrear,
  onCancelar,
}: AltaProductoProps): JSX.Element {
  const [nombre, setNombre] = useState(fila.nuevo_nombre ?? nombreCatalogo(fila.descripcion));
  const [rubroId, setRubroId] = useState<number | null>(fila.nuevo_rubro_id ?? rubroSugeridoId);
  const [codigo, setCodigo] = useState(fila.nuevo_codigo_interno ?? "");
  const [modo, setModo] = useState<ModoPrecio>(
    fila.nuevo_markup_pct !== null && fila.nuevo_precio_manual === null ? "margen" : "precio",
  );
  const [precio, setPrecio] = useState<number | null>(
    fila.nuevo_precio_manual === null ? null : aNumero(fila.nuevo_precio_manual),
  );
  const [margen, setMargen] = useState<number | null>(
    fila.nuevo_markup_pct === null ? null : aNumero(fila.nuevo_markup_pct),
  );
  const [intento, setIntento] = useState(false);

  const { costo, moneda: costoMoneda } = costoProducto;

  // El precio que va a quedar con ese margen: el mismo motor que el trigger.
  const precioConMargen = useMemo(() => {
    if (margen === null) return null;
    return calcularPrecio({
      costo,
      costoMoneda,
      markupTipo: "PORCENTAJE",
      markupValor: margen,
      markupMoneda: costoMoneda,
      precioMoneda: "ARS",
      redondeo: "NINGUNO",
      precioManual: null,
      cotizacion,
    }).precioVenta;
  }, [margen, costo, costoMoneda, cotizacion]);

  const datos: DatosAlta = {
    nombre,
    rubroId,
    codigoInterno: codigo.trim() === "" ? null : codigo.trim(),
    precio: modo === "precio" ? precio : null,
    markup: modo === "margen" ? margen : null,
  };
  const errores = validarAlta(datos);

  function crear(): void {
    setIntento(true);
    if (errores.length > 0) return;
    onCrear(datos);
  }

  return (
    // Va adentro de la tarjeta de la linea: por eso es `muted` y no una
    // segunda tarjeta default.
    <Card variant="muted" padding="normal" className="space-y-4">
      <p className="text-sm font-medium">Crear el producto</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Campo label="Nombre en el catálogo" id={`alta-nombre-${fila.id}`}>
            <Input
              id={`alta-nombre-${fila.id}`}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              autoComplete="off"
              className="uppercase"
            />
          </Campo>
        </div>

        <Campo label="Familia" id={`alta-rubro-${fila.id}`}>
          <Select
            value={rubroId === null ? SIN_RUBRO : String(rubroId)}
            onValueChange={(v) => setRubroId(v === SIN_RUBRO ? null : Number(v))}
          >
            <SelectTrigger id={`alta-rubro-${fila.id}`} aria-label="Familia">
              <SelectValue placeholder="Elegí la familia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SIN_RUBRO}>Elegí la familia</SelectItem>
              {rubros.map((r) => (
                <SelectItem key={r.id} value={String(r.id)}>
                  {r.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        <Campo
          label="Código interno"
          id={`alta-codigo-${fila.id}`}
          ayuda={
            fila.codigo_sugerido
              ? `Vacío, se le asigna el siguiente libre del rubro (hoy ${fila.codigo_sugerido}).`
              : "Vacío, el sistema le asigna uno."
          }
        >
          <Input
            id={`alta-codigo-${fila.id}`}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            placeholder={fila.codigo_sugerido ?? "automático"}
            autoComplete="off"
            className="tabular-nums"
          />
        </Campo>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">Precio de venta:</span>
          <Segmented
            opciones={MODOS}
            valor={modo}
            onChange={setModo}
            etiqueta="Cómo se fija el precio de venta"
          />
        </div>

        {modo === "precio" ? (
          <InputImporte
            label="Precio de venta"
            valor={precio}
            onValorChange={setPrecio}
            moneda="ARS"
            ayuda="Queda fijo: no se mueve aunque cambie el costo o el dólar."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end">
            <CampoCantidad
              label="Margen sobre el costo"
              valor={margen}
              onValorChange={setMargen}
              sufijo="%"
              ayuda="Vivo: si cambia el costo, el precio se recalcula."
            />
            <p className="text-sm">
              Queda en{" "}
              {precioConMargen === null ? (
                <strong>—</strong>
              ) : (
                <Importe monto={precioConMargen} moneda="ARS" className="font-semibold" />
              )}{" "}
              <span className="text-xs text-muted-foreground">
                (costo <Importe monto={costo} moneda={costoMoneda} tamano="sm" />)
              </span>
            </p>
          </div>
        )}
      </div>

      {intento && errores.length > 0 ? (
        <Aviso tono="destructive" compacto>
          <ul className="space-y-0.5">
            {errores.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Aviso>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancelar} disabled={guardando}>
          Cancelar
        </Button>
        <Button type="button" onClick={crear} cargando={guardando}>
          Crear y continuar
        </Button>
      </div>
    </Card>
  );
}
